/*
 * Created on Thu Oct 12 2023 10:21:05 AM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import apiClient, { API_ENDPOINTS, appendPath, dispatchAndResolve, rejectWithError } from "api";
import {
  buildAllValidProductsCriteria,
  buildCompanyStatusCriteria,
  buildCurriculumCriteria,
  buildDurationCriteria,
  buildMaxCapacityCriteria,
  buildProductCategoryCriteria,
  buildScheduledProductCriteria,
  buildSelfPacedValidProductsCriteria,
  buildServiceProductCriteria,
  buildUpcomingProductsCriteria,
  buildUserCurriculumByIdCriteria,
  buildUserCurriculumCriteria
} from "domains/catalog/services/productRequestBuilders";
import * as types from "./types.jsx";

const { company, courseSchedules, products, scheduledProducts } = API_ENDPOINTS;

const rejectProductsError = (error, label) => rejectWithError(error, label);
const publicCatalogRequestOptions = {
  credentials: "same-origin",
  skipAuth: true
};
const inFlightCatalogRequests = new Map();

const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${key}:${stableStringify(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
};

const runDedupedCatalogRequest = (key, request) => {
  if (inFlightCatalogRequests.has(key)) {
    return inFlightCatalogRequests.get(key);
  }

  const promise = request().finally(() => {
    inFlightCatalogRequests.delete(key);
  });
  inFlightCatalogRequests.set(key, promise);
  return promise;
};

// Course detail pages need one product, not the entire enriched catalog.
// Preserve the list endpoint's company/partner and availability filtering.
export const getCourseProductById = (companyId, primaryId, isScheduledProduct) => async () => {
  const productId = Number(primaryId);
  if (!companyId || !Number.isSafeInteger(productId) || productId <= 0) {
    throw new Error("Company and valid course ID are required");
  }
  const criteria = {
    ...(isScheduledProduct
      ? buildAllValidProductsCriteria(companyId)
      : buildCompanyStatusCriteria(companyId, "ACTIVE")),
    primaryId: productId,
  };
  const options = {
    credentials: "same-origin",
    skipAuth: true,
    ...(isScheduledProduct ? { query: { schedule_status: "ACTIVE" } } : {}),
  };
  return runDedupedCatalogRequest(
    `course-detail:${stableStringify({ criteria, options })}`,
    () => apiClient.post(products.getValidProducts, criteria, options)
  );
};

export const findProductsByCriteria = (criteria, {
  dispatchType = types.GET_ALL_PRODUCTS,
  requestOptions = {}
} = {}) => async (dispatch) => {
  try {
    const data = await apiClient.post(products.getAllProducts, criteria, requestOptions);
    return dispatchAndResolve(dispatch, dispatchType, data);
  } catch (error) {
    return rejectProductsError(error, "Error in findProductsByCriteria");
  }
};

export const getAllProducts = (companyId) => (
  findProductsByCriteria(
    buildCompanyStatusCriteria(companyId),
    { dispatchType: types.GET_ALL_PRODUCTS }
  )
);

export const getInterviewProducts = (companyId, categoryId = null) => async (dispatch) => {
  const criteria = buildCompanyStatusCriteria(companyId, "ACTIVE", {
    productCategorySubType: "INTERVIEW",
    ...(categoryId ? { category: { primaryId: categoryId } } : {})
  });

  try {
    const data = await apiClient.post(products.getAllProducts, criteria, publicCatalogRequestOptions);
    return dispatchAndResolve(dispatch, types.GET_ALL_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getInterviewProducts");
  }
};

export const getAllServiceProducts = (companyId) => (
  findProductsByCriteria(
    buildServiceProductCriteria(companyId),
    { dispatchType: types.GET_ALL_PRODUCTS }
  )
);

export const filterProduct = (companyId) => (
  findProductsByCriteria(
    buildCompanyStatusCriteria(companyId),
    { dispatchType: types.GET_ALL_PRODUCTS }
  )
);

export const getAllProductsByCode = (code) => async (dispatch) => {
  try {
    const data = await apiClient.get(products.getAllProducts, { query: { code } });
    return dispatchAndResolve(dispatch, types.GET_ALL_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getAllProductsByCode");
  }
};

export const getAllProductsByCategory = (companyId, categoryPrimaryId) => async (dispatch) => {
  try {
    const data = await apiClient.get(products.getAllProducts, {
      query: {
        companyId,
        categoryPrimaryId
      }
    });
    return dispatchAndResolve(dispatch, types.GET_ALL_PRODUCT_BY_CATEGORY, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getAllProductsByCategory");
  }
};

export const getAllScheduledProducts = (companyCode) => async (dispatch) => {
  try {
    const data = await apiClient.post(appendPath(products.getAllScheduledProducts, companyCode));
    return dispatchAndResolve(dispatch, types.GET_ALL_SCHEDULED_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getAllScheduledProducts");
  }
};

export const getCurriculumsByPrimaryId = (productId, entitiesToSkip = []) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      products.getCurriculums,
      buildCurriculumCriteria(productId, entitiesToSkip)
    );
    return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getCurriculumsByPrimaryId");
  }
};

const isEmptyCurriculumResult = (data) => {
  const value = data?.payload?.data ?? data?.data ?? data;
  return Array.isArray(value) ? value.length === 0 : !value;
};

// A Partner-course enrollment can come back empty when queried against a company
// that doesn't match how the curriculum was recorded (e.g. an older enrollment
// stored under the parent). When a distinct parentCompanyId is supplied, retry once
// against it before surfacing an empty result.
export const getUserCurriculumByUserId = (
  userId,
  scheduledProductId,
  companyId,
  type,
  deliverySubType,
  entitiesToSkip = [],
  primaryId,
  parentCompanyId
) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      products.getUserCurriculum,
      buildUserCurriculumCriteria({
        userId,
        scheduledProductId,
        companyId,
        type,
        deliverySubType,
        entitiesToSkip,
        primaryId
      })
    );
    if (isEmptyCurriculumResult(data) && parentCompanyId && parentCompanyId !== companyId) {
      const parentData = await apiClient.post(
        products.getUserCurriculum,
        buildUserCurriculumCriteria({
          userId,
          scheduledProductId,
          companyId: parentCompanyId,
          type,
          deliverySubType,
          entitiesToSkip,
          primaryId
        })
      );
      return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, parentData);
    }
    return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getUserCurriculumByUserId");
  }
};

export const getUserCurriculumById = (primaryId, entitiesToSkip = []) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      products.getUserCurriculum,
      buildUserCurriculumByIdCriteria(primaryId, entitiesToSkip)
    );
    return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getUserCurriculumById");
  }
};

export const findUserCurriculums = (criteria, parentCompanyId) => async (dispatch) => {
  try {
    const data = await apiClient.post(products.getUserCurriculum, criteria);
    const queriedCompanyId = criteria?.company?.primaryId;
    if (isEmptyCurriculumResult(data) && parentCompanyId && parentCompanyId !== queriedCompanyId) {
      const parentData = await apiClient.post(products.getUserCurriculum, {
        ...criteria,
        company: { primaryId: parentCompanyId }
      });
      return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, parentData);
    }
    return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in findUserCurriculums");
  }
};

export const getCurriculums = (productCode) => async (dispatch) => {
  try {
    const data = await apiClient.get(products.getCurriculums, { query: { productCode } });
    return dispatchAndResolve(dispatch, types.GET_CURRICULUMS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getCurriculums");
  }
};

export const getCompanyByParameters = (companyId, urlPrefix, id, code) => async () => {
  try {
    return apiClient.get(company.getCompanyByParameters, {
      query: {
        companyId,
        urlPrefix,
        id,
        code
      }
    });
  } catch (error) {
    return rejectProductsError(error, "Error in getCompanyByParameters");
  }
};

export const getContentById = (contentId) => async (dispatch) => {
  try {
    const data = await apiClient.get(appendPath(products.getContentById, contentId));
    return dispatchAndResolve(dispatch, types.GET_CONTENT_BY_ID, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getContentById");
  }
};

export const getScheduledProductsByProductCode = (productId) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      products.getScheduledProductsByProductCode,
      buildScheduledProductCriteria(productId)
    );
    return dispatchAndResolve(dispatch, types.GET_SCHEDULED_PRODUCTS_BY_PRODUCT_CODE, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getScheduledProductsByProductCode");
  }
};

export const getProductCategoryById = (id) => async (dispatch) => {
  try {
    const data = await apiClient.get(appendPath(products.getProductCategoryById, id));
    return dispatchAndResolve(dispatch, types.GET_PRODUCT_CATEGORY, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getProductCategoryById");
  }
};

export const getProductById = (productId) => async (dispatch) => {
  try {
    const data = await apiClient.get(appendPath(products.getProductById, productId));
    return dispatchAndResolve(dispatch, types.GET_PRODUCT_BY_ID, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getProductById");
  }
};

export const getAllProductCategories = (companyId) => async (dispatch) => {
  const criteria = buildProductCategoryCriteria(companyId);
  const requestKey = `getAllProductCategories:${stableStringify(criteria)}`;

  try {
    const data = await runDedupedCatalogRequest(
      requestKey,
      () => apiClient.post(products.getAllProductCategories, criteria, publicCatalogRequestOptions)
    );
    return dispatchAndResolve(dispatch, types.GET_ALL_PRODUCT_CATEGORIES, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getAllProductCategories");
  }
};

export const getProductCategory = (categoryId) => async (dispatch) => {
  try {
    const data = await apiClient.get(appendPath(products.getProductCategory, categoryId));
    return dispatchAndResolve(dispatch, types.GET_PRODUCT_CATEGORY, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getProductCategory");
  }
};

export const getAllValidProducts = (companyId, categoryId = null) => async (dispatch) => {
  const criteria = buildAllValidProductsCriteria(companyId, categoryId);
  const requestOptions = {
    ...publicCatalogRequestOptions,
    query: { schedule_status: "ACTIVE" }
  };
  const requestKey = `getAllValidProducts:${stableStringify({ criteria, requestOptions })}`;

  try {
    const data = await runDedupedCatalogRequest(
      requestKey,
      () => apiClient.post(products.getAllValidProducts, criteria, requestOptions)
    );
    return dispatchAndResolve(dispatch, types.GET_VALID_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getAllValidProducts");
  }
};

export const calculateEndDateOfScheduledProduct = async (timePeriod) => {
  try {
    return apiClient.post(scheduledProducts.calculateEndDateOfScheduledProduct, timePeriod);
  } catch (error) {
    return rejectProductsError(error, "Error in calculateEndDateOfScheduledProduct");
  }
};

export const fetchDuration = (daysOfSchedule, startTime, endTime, totalHours) => async () => {
  try {
    return apiClient.post(
      courseSchedules.fetchDuration,
      buildDurationCriteria(daysOfSchedule, startTime, endTime, totalHours)
    );
  } catch (error) {
    return rejectProductsError(error, "Error in fetchDuration");
  }
};

export const getValidProducts = (companyId, categoryId = null) => async (dispatch) => {
  const criteria = buildSelfPacedValidProductsCriteria(companyId, categoryId);
  const requestKey = `getValidProducts:${stableStringify(criteria)}`;

  try {
    const data = await runDedupedCatalogRequest(
      requestKey,
      () => apiClient.post(products.getValidProducts, criteria, publicCatalogRequestOptions)
    );
    return dispatchAndResolve(dispatch, types.GET_VALID_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getValidProducts");
  }
};

export const getUpcomingProducts = (companyId, categoryId = null) => async (dispatch) => {
  const criteria = buildUpcomingProductsCriteria(companyId, categoryId);
  const requestKey = `getUpcomingProducts:${stableStringify(criteria)}`;

  try {
    const data = await runDedupedCatalogRequest(
      requestKey,
      () => apiClient.post(products.getValidProducts, criteria, publicCatalogRequestOptions)
    );
    return dispatchAndResolve(dispatch, types.GET_VALID_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getUpcomingProducts");
  }
};

export const getScheduledProducts = (criteria) => async (dispatch) => {
  try {
    const data = await apiClient.post(products.getScheduledProductsByProductCode, criteria);
    return dispatchAndResolve(dispatch, types.GET_VALID_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getScheduledProducts");
  }
};

export const getMaxCapacity = (categoryId, companyId) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      products.getMaxCapacity,
      buildMaxCapacityCriteria(categoryId, companyId)
    );
    return dispatchAndResolve(dispatch, types.GET_MAX_CAPACITY, data);
  } catch (error) {
    return rejectProductsError(error, "Error in getMaxCapacity");
  }
};

export const searchProducts = (searchTerm) => async (dispatch) => {
  try {
    const data = await apiClient.post(products.searchProducts, undefined, {
      query: { term: searchTerm }
    });

    if (!Array.isArray(data) || data.length === 0) {
      return Promise.reject(new Error("No products found. Please try a different search term."));
    }

    return dispatchAndResolve(dispatch, types.SEARCH_PRODUCTS, data);
  } catch (error) {
    return rejectProductsError(error, "Error in searchProducts");
  }
};
